import { AdminStoreSyncLog } from '../../../app/models/Admin';

type Status = { label: string; tone?: 'attention' | 'info' | 'success' | 'critical' };
const normalize = (value?: string | null) => value?.trim().toUpperCase().replace(/[_ -]/g, '') || '';
const finished = (value: string) => ['COMPLETE', 'COMPLETED', 'PROCESSED'].includes(value);
const failed = (value: string) => ['FAILED', 'CANCELED', 'CANCELLED', 'EXPIRED', 'NOTFOUND'].includes(value);

export function syncLogStatus(log: AdminStoreSyncLog): { shopify: Status; local: Status } {
  const remote = normalize(log.shopifyOperationStatus);
  const local = normalize(log.localOperationStatus);
  const isBulk = !!log.bulkOperationGid || !!remote || /\/bulk(?:\/|$)/i.test(log.propertyName || '');
  const hasErrors = log.hasErrors || (log.errorCount || 0) > 0;
  // The importer only starts after Shopify has completed the export. Older workers
  // sometimes leave the remote field at CREATED/RUNNING throughout the import.
  const importStarted = local === 'PROCESSING';
  let shopify: Status = { label: 'Not a bulk job' };
  if (isBulk) {
    if (failed(remote)) shopify = { label: remote === 'NOTFOUND' ? 'Not found' : remote === 'EXPIRED' ? 'Expired' : remote.startsWith('CANCEL') ? 'Canceled' : 'Failed', tone: 'critical' };
    else if (finished(remote) || importStarted) shopify = { label: 'Complete', tone: 'success' };
    else if (remote === 'CREATED') shopify = { label: 'Queued at Shopify', tone: 'attention' };
    else if (remote === 'RUNNING') shopify = { label: 'Running on Shopify', tone: 'info' };
    else if (remote === 'CANCELING' || remote === 'CANCELLING') shopify = { label: 'Canceling', tone: 'attention' };
    else if (log.bulkOperationGid) shopify = { label: 'Submitted · status unknown', tone: 'attention' };
    else shopify = { label: 'Submission unconfirmed', tone: 'attention' };
  }

  let localStatus: Status;
  if (local === 'RETRYPENDING') localStatus = { label: 'Waiting to retry', tone: 'attention' };
  else if (local === 'CANCELING' || local === 'CANCELLING') localStatus = { label: 'Canceling', tone: 'attention' };
  else if (local === 'CANCELED' || local === 'CANCELLED') localStatus = { label: 'Canceled', tone: 'attention' };
  else if (local === 'EXPIRED') localStatus = { label: 'Expired · fresh sync requested', tone: 'attention' };
  else if (failed(local)) localStatus = { label: 'Failed', tone: 'critical' };
  else if (local === 'DEFERRED') localStatus = { label: 'Deferred for retry', tone: 'attention' };
  else if (!log.inProgress && hasErrors) localStatus = { label: 'Ended with errors', tone: 'critical' };
  else if (failed(remote)) localStatus = { label: 'Import blocked', tone: 'critical' };
  else if (!log.inProgress) localStatus = { label: 'Complete', tone: 'success' };
  else if (local === 'PROCESSING') localStatus = { label: 'Processing locally', tone: 'info' };
  else if (finished(local)) localStatus = { label: 'Finalizing', tone: 'info' };
  else if (finished(remote)) localStatus = { label: 'Waiting for local import', tone: 'attention' };
  else if (isBulk) localStatus = { label: 'Waiting for Shopify', tone: 'attention' };
  else localStatus = { label: 'In progress', tone: 'info' };

  return { shopify, local: localStatus };
}
